import React, { useEffect, useState } from 'react'
import { App, Button, Card, Dropdown, Space, Table, Tag, Upload } from 'antd'
import Text from 'antd/es/typography/Text'
import '../unit-tests-section.scss'
import {
    DeleteOutlined,
    DownOutlined,
    EditOutlined,
    ExclamationCircleOutlined,
    EyeOutlined,
    PlusOutlined,
    UploadOutlined,
} from '@ant-design/icons'
import { connect } from 'react-redux'
import { actions as unitTestsActions } from '../../../../../store/reducers/unit-tests.reducer'
import CreateStdinTestModal from './modals/create-test-modals/CreateStdinTestModal'
import CreateFunctionInTestModal from './modals/create-test-modals/CreateFunctionInTestModal'
import CreateFrameworkTestModal from './modals/create-test-modals/CreateFrameworkTestModal'
import { ASSIGNMENT_TEST_TYPES } from '../../../../../constants/assignment-test-types'
import { useParams } from 'react-router'
import EditStdinTestModal from './modals/edit-test-modals/EditStdinTestModal'
import EditFunctionInTestModal from './modals/edit-test-modals/EditFunctionInTestModal'
import EditFrameworkTestModal from './modals/edit-test-modals/EditFrameworkTestModal'
import { FRAMEWORK_TYPES } from '../../../../../constants/framework-types'
import { openNotificationWithIcon } from '../../../../../utils/notification.helper'
import { LANGUAGES } from '../../../../../constants/languages'
import CreateDatabaseInTestModal from './modals/create-test-modals/CreateDatabaseInTestModal'
import EditDatabaseInTestModal from './modals/edit-test-modals/EditDatabaseInTestModal'
import { Perm, State } from '../../../../../constants/assignment-permissions'
import Access, {
    useAssignmentAccess,
} from '../../../components/access/Access'
import ShowTipsSwitch from './components/ShowTipsSwitch'
import { ContentRules } from '../../../components/access/access-rules/assignment-rules'

const UnitTestsTable = ({
    assignment,
    setShowCreateStdinTestModal,
    setShowCreateFunctionInTestModal,
    setShowCreateFrameworkTestModal,
    setShowCreateDatabaseInTestModal,
    referenceSolution,
    unitTests,
    fetchUnitTests,
    deleteUnitTest,
    importConfig,
    showCreateStdinTestModal,
    showCreateFunctionInTestModal,
    showCreateFrameworkTestModal,
    showCreateDatabaseInTestModal,
    setCurrentlyEditingTestId,
    showEditStdinTestModal,
    showEditFunctionInTestModal,
    showEditFrameworkTestModal,
    showEditDatabaseInTestModal,
    setShowEditStdinTestModal,
    setShowEditFunctionInTestModal,
    setShowEditFrameworkTestModal,
    setShowEditDatabaseInTestModal,
}) => {
    const { modal } = App.useApp()
    const unitTestsTableData = []
    unitTests?.map(unitTest => {
        if (unitTest.type === ASSIGNMENT_TEST_TYPES.FRAMEWORK.value) {
            unitTest.test_data.test_cases.map((testCase, index) => {
                unitTestsTableData.push({
                    key: `${unitTest.id}-${testCase}`,
                    id: unitTest.id,
                    name: testCase,
                    type: unitTest.type,
                    input: Array.isArray(unitTest.test_data.input)
                        ? unitTest.test_data.input[index]
                        : unitTest.test_data.input,
                    functionName: unitTest.test_data.function_name,
                    framework: unitTest.test_data.framework,
                    testSuite: unitTest.test_data.test_suite,
                    mainClassName: unitTest.test_data.main_class_name,
                    cpuTimeLimit: unitTest.cpu_limit,
                    memoryLimit: unitTest.memory_limit,
                    exactMatch: unitTest.test_data.exact_match,
                    ignoreCase: unitTest.test_data.ignore_case,
                })
            })
        } else {
            unitTestsTableData.push({
                key: unitTest.id,
                id: unitTest.id,
                name: unitTest.scenario_name,
                type: unitTest.type,
                input: unitTest.test_data.input,
                functionName: unitTest.test_data.function_name,
                framework: unitTest.test_data.framework,
                testSuite: unitTest.test_data.test_suite,
                mainClassName: unitTest.test_data.main_class_name,
                cpuTimeLimit: unitTest.cpu_limit,
                memoryLimit: unitTest.memory_limit,
                exactMatch: unitTest.test_data.exact_match,
                ignoreCase: unitTest.test_data.ignore_case,
            })
        }
    })

    const { token } = useParams()
    const [isWaitingForUnitTests, setIsWaitingForUnitTests] = useState(false)
    const [isWaitingForDeleteTest, setIsWaitingForDeleteTest] = useState(false)

    const { hasAccess: hasEditingPermissions } = useAssignmentAccess([Perm.CAN_EDIT, State.NOT_PROCESSING, State.NOT_MBA, State.UNPUBLISHED])

    const handleEditTest = record => {
        setCurrentlyEditingTestId(record.id)
        if (record.type === ASSIGNMENT_TEST_TYPES.STDIN.value) {
            setShowEditStdinTestModal(true)
        } else if (record.type === ASSIGNMENT_TEST_TYPES.FUNCTION_IN.value) {
            if (assignment.language === LANGUAGES.SQL.value) {
                //database in tests on the backend are treated as function in tests
                setShowEditDatabaseInTestModal(true)
            } else {
                setShowEditFunctionInTestModal(true)
            }
        } else if (record.type === ASSIGNMENT_TEST_TYPES.FRAMEWORK.value) {
            setShowEditFrameworkTestModal(true)
        }
    }

    useEffect(() => {
        callFetchUnitTests()
    }, [])

    const callFetchUnitTests = async () => {
        setIsWaitingForUnitTests(true)
        await fetchUnitTests(token)
        setIsWaitingForUnitTests(false)
    }

    const handleDeleteTest = async id => {
        setIsWaitingForDeleteTest(true)
        await deleteUnitTest(id)
        setIsWaitingForDeleteTest(false)
    }

    const checkIfSomeTestHasField = field => unitTests.some(test => test[field] || test.test_data[field])

    const unitTestsTableColumns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => a.name.length - b.name.length,
            render: (name) => <Text ellipsis={{ tooltip: false }} title={name}>{name}</Text>,
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: type => {
                if (assignment.language === LANGUAGES.SQL.value && type === ASSIGNMENT_TEST_TYPES.FUNCTION_IN.value) {
                    //database in tests on the backend are treated as function in tests
                    return 'Database in'
                } else {
                    return ASSIGNMENT_TEST_TYPES[
                        Object.keys(ASSIGNMENT_TEST_TYPES).find(key => ASSIGNMENT_TEST_TYPES[key].value === type)
                    ].name
                }
            },
        },
        ...(checkIfSomeTestHasField('input')
            ? [
                  {
                      title: 'Input',
                      dataIndex: 'input',
                      key: 'input',
                      render: testSuite => (
                          <div
                              title={testSuite}
                              style={{
                                  maxWidth: 320,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                              }}
                          >
                              {testSuite}
                          </div>
                      ),
                  },
              ]
            : []),
        ...(checkIfSomeTestHasField('function_name')
            ? [
                  {
                      title: 'Function Name',
                      dataIndex: 'functionName',
                      key: 'functionName',
                  },
              ]
            : []),
        ...(checkIfSomeTestHasField('framework')
            ? [
                  {
                      title: 'Framework',
                      dataIndex: 'framework',
                      key: 'framework',
                  },
              ]
            : []),
        ...(checkIfSomeTestHasField('test_suite')
            ? [
                  {
                      title: 'Test Suite',
                      dataIndex: 'testSuite',
                      key: 'testSuite',
                      render: testSuite => (
                          <div
                              title={testSuite}
                              style={{
                                  maxWidth: 320,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                              }}
                          >
                              {testSuite}
                          </div>
                      ),
                  },
              ]
            : []),
        ...(checkIfSomeTestHasField('main_class_name')
            ? [
                  {
                      title: 'Main Class Name',
                      dataIndex: 'mainClassName',
                      key: 'mainClassName',
                  },
              ]
            : []),
        {
            title: 'CPU time limit, sec',
            dataIndex: 'cpuTimeLimit',
            key: 'cpuTimeLimit',
        },
        {
            title: 'Memory Limit, KB',
            dataIndex: 'memoryLimit',
            key: 'memoryLimit',
        },
        {
            title: 'Exact Match',
            dataIndex: 'exactMatch',
            key: 'exactMatch',
            width: '72px',
            render: exactMatch => <Tag color={exactMatch ? 'success' : 'error'}>{exactMatch ? 'True' : 'False'}</Tag>,
        },
        {
            title: 'Ignore Case',
            dataIndex: 'ignoreCase',
            key: 'ignoreCase',
            width: '76px',
            render: ignoreCase => <Tag color={ignoreCase ? 'success' : 'error'}>{ignoreCase ? 'True' : 'False'}</Tag>,
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => {
                const handleDeleteTestClick = () => {
                    modal.confirm({
                        title: 'Confirm Deletion',
                        icon: <ExclamationCircleOutlined />,
                        content: 'Are you sure you want to delete this test?',
                        okText: 'Delete',
                        okButtonProps: {
                            danger: true,
                        },
                        onOk() {
                            handleDeleteTest(record.id)
                        },
                        cancelText: 'Cancel',
                    })
                }

                return (
                    <>
                        <Button
                            shape='circle'
                            type='text'
                            onClick={() => handleEditTest(record)}
                            title={hasEditingPermissions ? 'Edit Test' : 'View Test'}
                        >
                            {hasEditingPermissions ? <EditOutlined className='primary-color' /> : <EyeOutlined />}
                        </Button>
                        {hasEditingPermissions && (
                            <Button
                                shape='circle'
                                type='text'
                                aria-label='delete test'
                                onClick={handleDeleteTestClick}
                                danger
                            >
                                <DeleteOutlined />
                            </Button>
                        )}
                    </>
                )
            },
        },
    ]

    const handleCreateStdinTestClick = () => {
        setShowCreateStdinTestModal(true)
    }

    const handleCreateDatabaseInTestClick = () => {
        setShowCreateDatabaseInTestModal(true)
    }

    const handleCreateFunctionInTestClick = () => {
        setShowCreateFunctionInTestModal(true)
    }

    const handleCreateFrameworkTestClick = () => {
        if (FRAMEWORK_TYPES.some(frameworkType => frameworkType.languages.includes(assignment.language))) {
            setShowCreateFrameworkTestModal(true)
        } else {
            openNotificationWithIcon('info', "There's no framework for that language")
        }
    }

    const createTestMenuItems = [
        ...(assignment.language === LANGUAGES.SQL.value
            ? [
                  {
                      key: 'databaseIn',
                      label: 'Database In',
                      onClick: handleCreateDatabaseInTestClick,
                  },
              ]
            : [
                  {
                      key: 'stdin',
                      label: 'Stdin',
                      onClick: handleCreateStdinTestClick,
                  },
                  {
                      key: 'function-in',
                      label: 'Function In',
                      onClick: handleCreateFunctionInTestClick,
                  },
              ]),
        {
            key: 'framework',
            label: 'Framework',
            onClick: handleCreateFrameworkTestClick,
            'data-test': 'create-framework-test-menu-item',
        },
    ]

    return (
        <Card
            title='Unit Tests'
            className='mb-16'
            extra={
                    <Space size={8}>
                        <Access rules={ContentRules.EDIT_NAMINGS}>
                            <ShowTipsSwitch />
                        </Access>
                        <Access rules={ContentRules.EDIT_GRADINGS}>
                            <Upload
                                showUploadList={false}
                                accept='.json'
                                beforeUpload={async file => {
                                    setIsWaitingForUnitTests(true)
                                    await importConfig(token, file)
                                    setIsWaitingForUnitTests(false)
                                    return false
                                }}
                                aria-label='import config'
                                disabled={!referenceSolution.name}
                            >
                                <Button disabled={!referenceSolution.name} aria-hidden='true' tabindex='-1'>
                                    <UploadOutlined aria-hidden='true' /> Import Config
                                </Button>
                            </Upload>
                        </Access>
                        <Access rules={ContentRules.EDIT_GRADINGS}>
                            <Dropdown
                                autoFocus
                                menu={{ items: createTestMenuItems }}
                                disabled={!referenceSolution.name}
                                trigger={['click']}
                            >
                                <Button type='primary' data-test="create-test-button">
                                    <PlusOutlined aria-hidden='true' /> Create Test <DownOutlined aria-hidden='true' />
                                </Button>
                            </Dropdown>
                        </Access>
                    </Space>
            }
        >
            <Table
                columns={unitTestsTableColumns}
                dataSource={unitTestsTableData}
                pagination={false}
                loading={isWaitingForUnitTests || isWaitingForDeleteTest || referenceSolution.isWaiting}
                locale={{
                    emptyText:
                        referenceSolution.name || referenceSolution.isWaiting
                            ? 'There are no unit tests'
                            : 'To create unit tests, you need to first upload reference solution ',
                }}
                scroll={{ x: true }}
            />
            {showCreateStdinTestModal && <CreateStdinTestModal />}
            {showCreateFunctionInTestModal && <CreateFunctionInTestModal />}
            {showCreateFrameworkTestModal && <CreateFrameworkTestModal />}
            {showCreateDatabaseInTestModal && <CreateDatabaseInTestModal />}
            {showEditStdinTestModal && <EditStdinTestModal />}
            {showEditFunctionInTestModal && <EditFunctionInTestModal />}
            {showEditFrameworkTestModal && <EditFrameworkTestModal />}
            {showEditDatabaseInTestModal && <EditDatabaseInTestModal />}
        </Card>
    )
}

const mapState = ({ assignment, unitTests }) => {
    return {
        assignment: assignment.assignment,
        referenceSolution: unitTests.referenceSolution,
        unitTests: unitTests.unitTests,
        showCreateStdinTestModal: unitTests.showCreateStdinTestModal,
        showCreateFunctionInTestModal: unitTests.showCreateFunctionInTestModal,
        showCreateFrameworkTestModal: unitTests.showCreateFrameworkTestModal,
        showCreateDatabaseInTestModal: unitTests.showCreateDatabaseInTestModal,
        showEditStdinTestModal: unitTests.showEditStdinTestModal,
        showEditFunctionInTestModal: unitTests.showEditFunctionInTestModal,
        showEditFrameworkTestModal: unitTests.showEditFrameworkTestModal,
        showEditDatabaseInTestModal: unitTests.showEditDatabaseInTestModal,
    }
}

const mapDispatch = dispatch => {
    return {
        setShowCreateStdinTestModal: value => {
            dispatch(unitTestsActions.setShowCreateStdinTestModal(value))
        },
        setShowCreateFunctionInTestModal: value => {
            dispatch(unitTestsActions.setShowCreateFunctionInTestModal(value))
        },
        setShowCreateFrameworkTestModal: value => {
            dispatch(unitTestsActions.setShowCreateFrameworkTestModal(value))
        },
        setShowCreateDatabaseInTestModal: value => {
            dispatch(unitTestsActions.setShowCreateDatabaseInTestModal(value))
        },
        fetchUnitTests: token => {
            return dispatch(unitTestsActions.fetchUnitTests(token))
        },
        deleteUnitTest: id => {
            return dispatch(unitTestsActions.deleteUnitTest(id))
        },
        importConfig: (token, file) => {
            return dispatch(unitTestsActions.importConfig(token, file))
        },
        setShowEditStdinTestModal: value => {
            dispatch(unitTestsActions.setShowEditStdinTestModal(value))
        },
        setCurrentlyEditingTestId: value => {
            dispatch(unitTestsActions.setCurrentlyEditingTestId(value))
        },
        setShowEditFunctionInTestModal: value => {
            dispatch(unitTestsActions.setShowEditFunctionInTestModal(value))
        },
        setShowEditFrameworkTestModal: value => {
            dispatch(unitTestsActions.setShowEditFrameworkTestModal(value))
        },
        setShowEditDatabaseInTestModal: value => {
            dispatch(unitTestsActions.setShowEditDatabaseInTestModal(value))
        },
    }
}

export default connect(mapState, mapDispatch)(UnitTestsTable)
